import {
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  MessageSquarePlus,
  Play,
  Power,
  Send,
  SkipForward,
  Truck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type {
  OrderWorkflow,
  OrderWorkflowStage,
  OrderWorkflowSubStep,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { outcomeKey, stageActions, type StageAction } from '../schemas/stage-rules';

import {
  AttemptsList,
  CustomerAnswer,
  FilesList,
  NotesList,
  ParcelsList,
  QcSubmissionsList,
} from './stage-details';
import { DelayedBadge, StageStatusBadge } from './stage-status-badge';

/** Guided-tour targets on the current stage's buttons. */
const TOUR_BY_ACTION: Partial<Record<StageAction, string>> = {
  qc: 'orders.detail.production.qc',
  sendFiles: 'orders.detail.production.send-files',
  parcels: 'orders.detail.production.parcels',
  due: 'orders.detail.production.due-date',
};

export interface StageItemProps {
  workflow: OrderWorkflow;
  stage: OrderWorkflowStage;
  sub?: OrderWorkflowSubStep;
  pendingKey: string | null;
  /** The order lines, to name what a parcel carried. */
  lines?: ReadonlyArray<{ id: string; title: string }>;
  onAction: (action: StageAction, stage: OrderWorkflowStage, sub?: OrderWorkflowSubStep) => void;
}

interface ActionButton {
  label: string;
  aria: string;
  icon: ReactNode;
  primary?: boolean;
}

function useActionButtons(name: string, qcDone: boolean, reviewSent: boolean) {
  const { t } = useTranslation('production');
  const buttons: Record<StageAction, ActionButton> = {
    start: {
      label: t('actions.start'),
      aria: t('actions.startLabel', { name }),
      icon: <Play aria-hidden />,
      primary: true,
    },
    complete: {
      label: t('actions.complete'),
      aria: t('actions.completeLabel', { name }),
      icon: <CheckCircle2 aria-hidden />,
      primary: true,
    },
    deliver: {
      label: t('actions.deliver'),
      aria: t('actions.deliverLabel', { name }),
      icon: <CheckCircle2 aria-hidden />,
      primary: true,
    },
    recordAnswer: {
      label: t('actions.recordAnswer'),
      aria: t('actions.recordAnswerLabel', { name }),
      icon: <CheckCircle2 aria-hidden />,
    },
    skip: {
      label: t('actions.skip'),
      aria: t('actions.skipLabel', { name }),
      icon: <SkipForward aria-hidden />,
    },
    enable: {
      label: t('actions.enable'),
      aria: t('actions.enableLabel', { name }),
      icon: <Power aria-hidden />,
    },
    due: {
      label: t('actions.due'),
      aria: t('actions.dueLabel', { name }),
      icon: <CalendarClock aria-hidden />,
    },
    note: {
      label: t('actions.note'),
      aria: t('actions.noteLabel', { name }),
      icon: <MessageSquarePlus aria-hidden />,
    },
    sendFiles: {
      label: t('actions.sendFiles'),
      aria: t('actions.sendFilesLabel', { name }),
      icon: <Send aria-hidden />,
      primary: true,
    },
    sendReview: {
      label: reviewSent ? t('actions.resendReview') : t('actions.sendReview'),
      aria: t('actions.sendReviewLabel', { name }),
      icon: <Send aria-hidden />,
      primary: !reviewSent,
    },
    qc: {
      label: qcDone ? t('actions.qcResubmit') : t('actions.qc'),
      aria: t('actions.qcLabel', { name }),
      icon: <ClipboardCheck aria-hidden />,
      primary: !qcDone,
    },
    parcels: {
      label: t('actions.parcels'),
      aria: t('actions.parcelsLabel', { name }),
      icon: <Truck aria-hidden />,
      primary: true,
    },
  };
  return buttons;
}

/** One stage or sub-step: state, timing, outcome and the actions its type and state allow. */
export function StageItem({ workflow, stage, sub, pendingKey, lines, onAction }: StageItemProps) {
  const { t } = useTranslation(['production', 'workflows']);
  const format = useFormat();
  const { can } = usePermissions();
  const node = sub ?? stage;
  const run = node.run;
  const outcome = outcomeKey(run.outcome);
  const actions = can('orders.write') ? stageActions(workflow, stage, sub) : [];
  const buttons = useActionButtons(
    node.name,
    run.qcSubmissions.length > 0,
    run.attempts.length > 0,
  );
  const current = !sub && workflow.currentStageKey === stage.key;
  const skippedOutcome =
    run.status === 'skipped' && (outcome === 'skipped' || outcome === 'not_needed');

  return (
    <li
      aria-label={node.name}
      aria-current={current ? 'step' : undefined}
      className={cn(
        'flex flex-col gap-2',
        !sub && 'rounded-lg border p-3',
        !sub && current && 'border-primary/50 bg-primary/5',
        sub && 'border-l-2 pl-3',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('font-medium', sub && 'text-sm')}>{node.name}</span>
        {sub ? null : (
          <span className="text-muted-foreground text-xs">{t(`workflows:type.${stage.type}`)}</span>
        )}
        <StageStatusBadge status={run.status} />
        {run.delayed ? <DelayedBadge /> : null}
        {node.optional ? <Badge variant="outline">{t('stage.optional')}</Badge> : null}
        {outcome && !skippedOutcome && outcome !== 'closed_with_stage' ? (
          <Badge variant="outline">{t(`outcome.${outcome}`)}</Badge>
        ) : null}
      </div>
      <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
        {run.dueAt && !['completed', 'skipped', 'stopped'].includes(run.status) ? (
          <span
            title={format.utc(run.dueAt)}
            className={cn(run.delayed && 'text-destructive font-medium')}
          >
            {t('stage.due', { when: format.dateTime(run.dueAt) })}
            {run.dueAtSource === 'manual' ? ` ${t('stage.dueManual')}` : null}
          </span>
        ) : null}
        {run.startedAt ? (
          <span title={format.utc(run.startedAt)}>
            {t('stage.started', { when: format.dateTime(run.startedAt) })}
          </span>
        ) : null}
        {run.completedAt && run.status === 'completed' ? (
          <span title={format.utc(run.completedAt)}>
            {t('stage.completed', { when: format.dateTime(run.completedAt) })}
            {run.completedBy ? ` ${t('stage.completedBy', { name: run.completedBy.name })}` : null}
          </span>
        ) : null}
      </div>
      {run.delayReason ? (
        <p
          className="text-sm"
          data-tour={current ? 'orders.detail.production.delay-reason' : undefined}
        >
          {t('stage.delayReason', { reason: run.delayReason })}
        </p>
      ) : null}
      {run.skipReason ? (
        <p className="text-muted-foreground text-sm">
          {t('stage.skipReason', { reason: run.skipReason })}
        </p>
      ) : null}
      {run.certificateNumber ? (
        <p className="text-sm">{t('stage.certificate', { number: run.certificateNumber })}</p>
      ) : null}
      <CustomerAnswer run={run} />
      {sub ? null : (
        <>
          {stage.type === 'send_files' ? (
            <div data-tour={current ? 'orders.detail.production.files' : undefined}>
              <FilesList files={run.files} label={t('stage.files', { name: node.name })} />
            </div>
          ) : null}
          {stage.type === 'qc' ? <QcSubmissionsList stage={stage} /> : null}
          {stage.type === 'customer_review' ? <AttemptsList stage={stage} /> : null}
          {stage.type === 'shipping' ? <ParcelsList stage={stage} lines={lines} /> : null}
          {stage.type !== 'send_files' && run.files.length > 0 ? (
            <FilesList files={run.files} label={t('stage.files', { name: node.name })} />
          ) : null}
        </>
      )}
      <NotesList run={run} name={node.name} />
      {actions.length > 0 ? (
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label={t('stage.actionsFor', { name: node.name })}
          data-tour={current ? 'orders.detail.production.actions' : undefined}
        >
          {actions.map((action) => {
            const button = buttons[action];
            return (
              <Button
                key={action}
                type="button"
                size="sm"
                variant={button.primary ? 'default' : 'outline'}
                aria-label={button.aria}
                data-tour={current ? TOUR_BY_ACTION[action] : undefined}
                disabled={pendingKey === node.key}
                onClick={() => onAction(action, stage, sub)}
              >
                {button.icon}
                {button.label}
              </Button>
            );
          })}
        </div>
      ) : null}
      {!sub && stage.subSteps.length > 0 ? (
        <ol
          className="mt-1 flex flex-col gap-3"
          aria-label={t('stage.subSteps', { name: stage.name })}
        >
          {stage.subSteps.map((subStep) => (
            <StageItem
              key={subStep.key}
              workflow={workflow}
              stage={stage}
              sub={subStep}
              pendingKey={pendingKey}
              onAction={onAction}
            />
          ))}
        </ol>
      ) : null}
    </li>
  );
}
